import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { KeyRef, Song } from '@stage/contracts';
import { ApiError } from '../api/client.js';
import type { SongInput } from '../api/showClient.js';
import { useShowClient } from '../hooks/useShowClient.js';

/**
 * Repertoire management (§18.1). Private master song data: list/search, add/edit/delete a song, and
 * bulk CSV import (§18.1, §17.6). Fully keyboard-operable — every control is a native button/input
 * with an associated label (§35).
 */

interface Draft {
  songId: string | null;
  title: string;
  originalArtist: string;
  tags: string;
  genre: string;
  keyDisplay: string;
  active: boolean;
  requestable: boolean;
}

const EMPTY_DRAFT: Draft = {
  songId: null,
  title: '',
  originalArtist: '',
  tags: '',
  genre: '',
  keyDisplay: '',
  active: true,
  requestable: true,
};

const TONICS = ['C', 'C#/Db', 'D', 'D#/Eb', 'E', 'F', 'F#/Gb', 'G', 'G#/Ab', 'A', 'A#/Bb', 'B'];

/** Best-effort parse of a free-text key (e.g. "F# minor") into a {@link KeyRef} (§18.1). */
function parseKey(display: string): KeyRef | undefined {
  const trimmed = display.trim();
  if (!trimmed) return undefined;
  const head = trimmed.replace(/\s+/g, ' ');
  // Prefer the longest matching tonic prefix so "C#" is not shadowed by "C".
  let tonic = 'C';
  let best = 0;
  for (const t of TONICS) {
    for (const spelling of t.split('/')) {
      if (head.toUpperCase().startsWith(spelling.toUpperCase()) && spelling.length > best) {
        best = spelling.length;
        tonic = t;
      }
    }
  }
  const mode = /min/i.test(trimmed) ? 'minor' : 'major';
  return { tonic: tonic as KeyRef['tonic'], mode, display: trimmed };
}

function draftToInput(draft: Draft): SongInput {
  const tags = draft.tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const key = parseKey(draft.keyDisplay);
  const input: SongInput = {
    title: draft.title.trim(),
    tags,
    active: draft.active,
    requestableDefault: draft.requestable,
  };
  if (draft.originalArtist.trim()) input.originalArtist = draft.originalArtist.trim();
  if (draft.genre.trim()) input.genre = draft.genre.trim();
  if (key) input.key = key;
  return input;
}

function songToDraft(song: Song): Draft {
  return {
    songId: song.songId,
    title: song.title,
    originalArtist: song.originalArtist ?? '',
    tags: song.tags.join(', '),
    genre: song.genre ?? '',
    keyDisplay: song.key?.display ?? '',
    active: song.active,
    requestable: song.requestableDefault,
  };
}

export function RepertoirePage() {
  const { artistId = '' } = useParams();
  const client = useShowClient();

  const [songs, setSongs] = useState<Song[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [csv, setCsv] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(
    null,
  );
  const [importError, setImportError] = useState<string | null>(null);

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const page = await client.listSongs(artistId);
      setSongs(page.items);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : 'Could not load songs.');
    } finally {
      setLoading(false);
    }
  }, [client, artistId]);

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoading(true);
    try {
      const page = await client.listSongs(artistId, nextCursor);
      setSongs((prev) => [...prev, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : 'Could not load more songs.');
    } finally {
      setLoading(false);
    }
  }, [client, artistId, nextCursor]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return songs;
    return songs.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        (s.originalArtist?.toLowerCase().includes(q) ?? false) ||
        s.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [songs, search]);

  const resetForm = useCallback(() => {
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  }, []);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!draft.title.trim()) return;
      setSaving(true);
      setFormError(null);
      setNotice(null);
      try {
        const input = draftToInput(draft);
        if (draft.songId) {
          const updated = await client.updateSong(artistId, draft.songId, input);
          setSongs((prev) => prev.map((s) => (s.songId === updated.songId ? updated : s)));
          setNotice(`Saved “${updated.title}”.`);
        } else {
          const created = await client.createSong(artistId, input);
          setSongs((prev) => [created, ...prev]);
          setNotice(`Added “${created.title}”.`);
        }
        resetForm();
      } catch (err) {
        if (err instanceof ApiError) setFormError(err);
        else setFormError(null);
      } finally {
        setSaving(false);
      }
    },
    [client, artistId, draft, resetForm],
  );

  const onDelete = useCallback(
    async (song: Song) => {
      if (!window.confirm(`Delete “${song.title}” from the repertoire?`)) return;
      try {
        await client.deleteSong(artistId, song.songId);
        setSongs((prev) => prev.filter((s) => s.songId !== song.songId));
        if (draft.songId === song.songId) resetForm();
        setNotice(`Deleted “${song.title}”.`);
      } catch (err) {
        setListError(err instanceof ApiError ? err.message : 'Could not delete song.');
      }
    },
    [client, artistId, draft.songId, resetForm],
  );

  const onImport = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!csv.trim()) return;
      setImporting(true);
      setImportError(null);
      setImportResult(null);
      try {
        const result = await client.importSongs(artistId, csv);
        setImportResult(result);
        setCsv('');
        void loadFirst();
      } catch (err) {
        setImportError(err instanceof ApiError ? err.message : 'Import failed.');
      } finally {
        setImporting(false);
      }
    },
    [client, artistId, csv, loadFirst],
  );

  const onFile = useCallback((file: File | undefined) => {
    if (!file) return;
    void file.text().then((text) => setCsv(text));
  }, []);

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to="/app">
              Overview
            </Link>{' '}
            / Repertoire
          </p>
          <h1>Repertoire</h1>
          <p className="muted">Private master song list for this artist.</p>
        </div>
      </header>

      <section className="card">
        <div className="module-head">
          <h2>{draft.songId ? 'Edit song' : 'Add a song'}</h2>
          {notice && (
            <span className="muted" aria-live="polite">
              {notice}
            </span>
          )}
        </div>
        <form className="song-form" onSubmit={onSubmit}>
          <label>
            Title
            <input
              value={draft.title}
              required
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder="Song title"
            />
          </label>
          <label>
            Original artist
            <input
              value={draft.originalArtist}
              onChange={(e) => setDraft((d) => ({ ...d, originalArtist: e.target.value }))}
              placeholder="e.g. Frank Sinatra"
            />
          </label>
          <label>
            Tags (comma-separated)
            <input
              value={draft.tags}
              onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))}
              placeholder="jazz, slow, closer"
            />
          </label>
          <label>
            Genre
            <input
              value={draft.genre}
              onChange={(e) => setDraft((d) => ({ ...d, genre: e.target.value }))}
              placeholder="Jazz"
            />
          </label>
          <label>
            Key
            <input
              value={draft.keyDisplay}
              onChange={(e) => setDraft((d) => ({ ...d, keyDisplay: e.target.value }))}
              placeholder="e.g. F# minor"
            />
          </label>
          <div className="song-form__toggles">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))}
              />
              Active
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={draft.requestable}
                onChange={(e) => setDraft((d) => ({ ...d, requestable: e.target.checked }))}
              />
              Requestable by default
            </label>
          </div>
          <div className="button-row">
            <button type="submit" className="action--primary" disabled={saving || !draft.title.trim()}>
              {draft.songId ? 'Save changes' : 'Add song'}
            </button>
            {draft.songId && (
              <button type="button" className="ghost" onClick={resetForm} disabled={saving}>
                Cancel edit
              </button>
            )}
          </div>
          {formError && (
            <div className="validation" role="alert">
              <p className="error">{formError.message}</p>
              {formError.fieldErrors.length > 0 && (
                <ul>
                  {formError.fieldErrors.map((fe, i) => (
                    <li key={`${fe.path}-${i}`}>
                      <code>{fe.path}</code>: {fe.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </form>
      </section>

      <section className="card">
        <div className="module-head">
          <h2>Songs</h2>
          <label className="search-field">
            <span className="visually-hidden">Search songs</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, artist, tag…"
            />
          </label>
        </div>
        {listError && <p className="error">{listError}</p>}
        {songs.length === 0 && !loading ? (
          <p className="muted">No songs yet. Add one above or import a CSV.</p>
        ) : (
          <ul className="song-list">
            {filtered.map((song) => (
              <li key={song.songId} className="song-row">
                <div className="song-row__main">
                  <p className="song-row__title">
                    {song.title}
                    {!song.active && <span className="badge small song-row__flag">inactive</span>}
                    {song.requestableDefault && (
                      <span className="badge small">requestable</span>
                    )}
                  </p>
                  <p className="muted song-row__meta">
                    {song.originalArtist ?? '—'}
                    {song.genre ? ` · ${song.genre}` : ''}
                    {song.key?.display ? ` · ${song.key.display}` : ''}
                    {song.tags.length > 0 ? ` · ${song.tags.join(', ')}` : ''}
                  </p>
                </div>
                <div className="song-row__actions">
                  <button type="button" onClick={() => setDraft(songToDraft(song))}>
                    Edit
                  </button>
                  <button type="button" className="danger" onClick={() => void onDelete(song)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {loading && <p aria-busy="true">Loading…</p>}
        {nextCursor && !loading && (
          <button type="button" className="ghost" onClick={() => void loadMore()}>
            Load more
          </button>
        )}
      </section>

      <section className="card">
        <h2>Import from CSV</h2>
        <p className="help">
          Paste rows or choose a file. Header row: <code>title,originalArtist,tags,genre,key</code>.
          Tags are separated by <code>|</code> within the tags cell.
        </p>
        <form className="csv-import" onSubmit={onImport}>
          <label>
            <span className="visually-hidden">CSV upload</span>
            <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          <label>
            CSV content
            <textarea
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              rows={6}
              placeholder={'title,originalArtist,tags,genre,key\nFly Me to the Moon,Frank Sinatra,jazz|closer,Jazz,C major'}
            />
          </label>
          <div className="button-row">
            <button type="submit" disabled={importing || !csv.trim()}>
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
          {importResult && (
            <p className="import-result" aria-live="polite">
              Imported <strong>{importResult.imported}</strong>, skipped{' '}
              <strong>{importResult.skipped}</strong>.
            </p>
          )}
          {importError && <p className="error">{importError}</p>}
        </form>
      </section>
    </main>
  );
}
